import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import PublicLayout from "../components/PublicLayout";
import { HeaderLogo } from "../components/BrandLogos";
import RoadRibbon from "../components/RoadRibbon";
import Testimonials from "../components/Testimonials";
import RouteSignCard from "../components/RouteSignCard";
import WhyChooseUs from "../components/WhyChooseUs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Bus, Shield, Clock, Sparkles, ArrowRight, Users, Star, Award, Phone, MapPin, ShieldCheck, Sun } from "lucide-react";
import WhatsAppIcon from "../components/WhatsAppIcon";

const FEATURES = [
  { icon: Shield, title: "Safe Travels", sub: "Priority #1" },
  { icon: ShieldCheck, title: "Your Safety", sub: "Our Priority" },
  { icon: Users, title: "Experienced", sub: "Drivers" },
  { icon: Star, title: "Comfort", sub: "You Deserve" },
];

// Fleet images matching the poster — Maruti Suzuki Ertiga & Force Motors Tempo Traveller (Wikipedia)
const HERO_ERTIGA = "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Suzuki_Ertiga_NC_FL_1.5_GLX_Hybrid_Snow_White_Pearl.jpg/1280px-Suzuki_Ertiga_NC_FL_1.5_GLX_Hybrid_Snow_White_Pearl.jpg";
const HERO_TRAVELLER = "https://upload.wikimedia.org/wikipedia/commons/1/13/IMG-20190215-WA0015.jpg";

export default function Home() {
  const t = useT();
  const nav = useNavigate();
  const [form, setForm] = useState({ from_place: "Palus", to_place: "Pune", travel_date: new Date().toISOString().slice(0,10), passengers: 2, ac: "any" });
  const [tours, setTours] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [company, setCompany] = useState({ name:"Shruti Tours & Travels", phone:"", tagline:"Safe Travels, Happy Memories" });

  useEffect(() => {
    api.get("/tours").then(r => setTours(r.data.filter(t=>t.featured).slice(0,3))).catch(()=>{});
    api.get("/routes").then(r => setRoutes(r.data.slice(0,6))).catch(()=>{});
    api.get("/routes/featured").then(r => setFeatured(r.data)).catch(()=>{});
    api.get("/company").then(r => setCompany(r.data)).catch(()=>{});
  }, []);

  const search = (e) => {
    e.preventDefault();
    const q = new URLSearchParams(form).toString();
    nav(`/book?${q}`);
  };

  const waNumber = (company.phone || "").replace(/\D/g,'');
  const phoneClean = (company.phone || "").replace(/^\+91\s*/,'');

  return (
    <PublicLayout>
      {/* HERO + trailing road ribbon locked to one viewport (no next section peeks in) */}
      <div className="min-h-[calc(100dvh-8rem)] flex flex-col">
      {/* HERO — side-by-side: emblem left, content right */}
      <section className="relative bg-white flex-1 flex items-center">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[720px] bg-gradient-to-b from-[#dfe9ff] via-[#f5f7ff] to-white" />
          <Sun className="sun-rise absolute top-10 right-[6%] w-16 h-16 md:w-24 md:h-24 text-amber-400" strokeWidth={1.5} />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 py-3 lg:py-4 grid lg:grid-cols-2 gap-4 items-center">
          {/* LEFT: emblem + tagline */}
          <div className="text-center lg:text-left">
            <div className="hover-zoom overflow-visible" data-testid="hero-emblem">
              <HeaderLogo size="lg" className="mx-auto lg:mx-0" />
            </div>
            <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
              {t("hero.title_1")} {t("hero.title_route")} {t("hero.title_2")} {t("hero.title_3")}
            </p>
            <div className="mt-3 flex flex-wrap justify-center lg:justify-start gap-2">
              <Link to="/book" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--brand-orange)] hover:bg-[var(--brand-orange-2)] text-white font-bold text-sm shadow-lg shadow-orange-200 hover:-translate-y-0.5" data-testid="hero-book-cta">{t("hero.book_ride")} <ArrowRight className="w-4 h-4" /></Link>
              {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#25D366] hover:bg-emerald-600 text-white font-bold text-sm hover:-translate-y-0.5" data-testid="hero-whatsapp-cta"><WhatsAppIcon className="w-4 h-4" /> {t("hero.whatsapp")}{phoneClean ? ` ${phoneClean}`:""}</a>}
              {company.phone && <a href={`tel:${company.phone}`} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--brand-navy)] hover:bg-[var(--brand-navy-2)] text-white font-bold text-sm hover:-translate-y-0.5" data-testid="hero-call-cta"><Phone className="w-4 h-4" /> {t("hero.call")}</a>}
            </div>
          </div>

          {/* RIGHT: routes + booking box */}
          <div>
            <div className="grid grid-cols-2 gap-2" data-testid="hero-daily-slots">
              {(() => {
                const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
                const today = new Date();
                const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
                // Try to source real times from Palus↔Pune routes if admin has added them
                const findTimes = (from, to) => {
                  const r = routes.find(x => (x.from_place || "").toLowerCase() === from && (x.to_place || "").toLowerCase() === to);
                  if (!r) return null;
                  const times = (r.departure_times && r.departure_times.length ? r.departure_times : [r.departure_time]).filter(Boolean);
                  return times.length ? times.join(" · ") : null;
                };
                const dateSlots = [{ label: t("hero.today"), date: fmt(today) }, { label: t("hero.tomorrow"), date: fmt(tomorrow) }];
                const palusPuneTimes = findTimes("palus", "pune");
                const punePalusTimes = findTimes("pune", "palus");
                const timeText = (tt) => tt ? `${t("hero.daily_service")} · ${tt}` : `${t("hero.daily_service")} · ${t("hero.time_shortly")}`;
                return dateSlots.flatMap((s, idx) => [
                  <RouteSignCard key={`pp-${idx}`} from="PALUS" to="PUNE" subtitle={`${timeText(palusPuneTimes)} · ${s.label}`} />,
                  <RouteSignCard key={`pl-${idx}`} from="PUNE" to="PALUS" tone="orange" subtitle={`${timeText(punePalusTimes)} · ${s.label}`} />,
                ]);
              })()}
            </div>
            <form onSubmit={search} className="mt-3 bg-white rounded-2xl shadow-2xl border border-slate-200 p-3 grid grid-cols-2 gap-2.5" data-testid="hero-booking-form">
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t("hero.from")}</Label>
                <Input value={form.from_place} onChange={e=>setForm({...form, from_place: e.target.value})} placeholder="City" data-testid="input-from" className="h-10" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t("hero.to")}</Label>
                <Input value={form.to_place} onChange={e=>setForm({...form, to_place: e.target.value})} placeholder="City" data-testid="input-to" className="h-10" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t("hero.date")}</Label>
                <Input type="date" value={form.travel_date} onChange={e=>setForm({...form, travel_date: e.target.value})} data-testid="input-date" className="h-10" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">{t("hero.pax")}</Label>
                <Input type="number" min="1" value={form.passengers} onChange={e=>setForm({...form, passengers: parseInt(e.target.value)||1})} data-testid="input-passengers" className="h-10" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">AC</Label>
                <Select value={form.ac} onValueChange={v=>setForm({...form, ac: v})}>
                  <SelectTrigger data-testid="select-ac" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="true">AC</SelectItem>
                    <SelectItem value="false">Non-AC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Button type="submit" className="w-full bg-[var(--brand-orange)] hover:bg-[var(--brand-orange-2)] h-10 rounded-lg font-bold shadow-lg shadow-orange-200 hover:-translate-y-0.5" data-testid="search-vehicles-btn">{t("hero.search")} <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </form>

            {/* Feature strip — 4-across single row so bottom road ribbon stays in the fold */}
            <div className="mt-2 rounded-2xl bg-[var(--brand-navy)] text-white p-2 grid grid-cols-4 gap-2">
              {FEATURES.map((f, i) => (
                <div key={i} className="feature-strip-icon flex items-center gap-2" data-testid={`feature-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-white/10 border border-white/15 grid place-items-center flex-shrink-0">
                    <f.icon className="w-3.5 h-3.5 text-[var(--brand-orange)]" />
                  </div>
                  <div className="leading-tight min-w-0">
                    <div className="font-display font-bold text-xs truncate">{f.title}</div>
                    <div className="text-[10px] text-white/70 truncate">{f.sub}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-[var(--brand-navy)] px-3 py-1.5 text-[11px] font-bold shadow" data-testid="group-discount-banner">
              🎉 Group Discount: 10% off automatically when you book for 8+ passengers.
            </div>
          </div>
        </div>
      </section>

      {/* Road ribbon */}
      <RoadRibbon withCar={true} text="Daily Service · Palus ↔ Pune · Book Now" />
      </div>

      {/* Testimonials */}
      <Testimonials />

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-4 mt-16 grid md:grid-cols-4 gap-6">
        {[
          { icon: Bus, title: "5–30 Seater Fleet", sub: "Ertiga, Traveller, Mini-Bus, Coach" },
          { icon: Shield, title: "Verified Drivers", sub: "Licensed & experienced" },
          { icon: Clock, title: "On-time Departures", sub: "Live tracking soon" },
          { icon: Sparkles, title: "Pilgrim Tours", sub: "Ashtavinayak · Balaji · Vaishnodevi" },
        ].map((f,i) => (
          <div key={i} className="p-6 rounded-xl bg-white border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-transform" data-testid={`trust-card-${i}`}>
            <f.icon className="w-6 h-6 text-[var(--brand-orange)]" />
            <div className="font-display font-bold text-lg mt-3 text-[var(--brand-navy)]">{f.title}</div>
            <div className="text-sm text-slate-500 mt-1">{f.sub}</div>
          </div>
        ))}
      </section>

      {/* Featured Tours */}
      {tours.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 mt-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--brand-orange)] font-semibold">Upcoming Tours</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-[var(--brand-navy)]">Poster-tour packages, curated for you</h2>
            </div>
            <Link to="/tours" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[var(--brand-navy)] hover:text-[var(--brand-orange)]" data-testid="view-all-tours">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tours.map(t => (
              <Link to={`/tours/${t.id}`} key={t.id} className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:-translate-y-1 hover:shadow-xl transition-transform" data-testid={`tour-card-${t.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={t.poster_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" alt={t.title} />
                </div>
                {t.badge && <div className="absolute top-3 left-3 bg-[var(--brand-orange)] text-white text-xs px-2.5 py-1 rounded-full font-semibold">{t.badge}</div>}
                <div className="p-5">
                  <div className="font-display font-bold text-lg text-[var(--brand-navy)]">{t.title}</div>
                  <div className="text-sm text-slate-500 mt-1">{t.subtitle}</div>
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.days}D / {t.nights}N</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.destination}</span>
                    {t.with_stay && <span className="inline-flex items-center gap-1 text-emerald-600">✓ Stay included</span>}
                  </div>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <div className="text-xs text-slate-500">Per person</div>
                      <div className="text-xl font-black text-[var(--brand-navy)]">₹{t.price_per_person.toLocaleString()}</div>
                    </div>
                    <span className="text-[var(--brand-orange)] font-semibold text-sm inline-flex items-center gap-1">View <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Daily Routes with sign cards */}
      <section className="max-w-7xl mx-auto px-4 mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--brand-orange)] font-semibold">Daily Route Services</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-[var(--brand-navy)]">Book a seat, board on time</h2>
          </div>
          <Link to="/routes" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[var(--brand-navy)] hover:text-[var(--brand-orange)]" data-testid="view-all-routes">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {routes.length === 0 ? (
          <div className="p-8 md:p-12 rounded-2xl bg-white border-2 border-dashed border-slate-300 text-center" data-testid="routes-empty-state">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--brand-orange)]/10 text-[var(--brand-orange)] text-[10px] uppercase tracking-widest font-bold">Coming Soon</div>
            <h3 className="mt-3 text-xl sm:text-2xl font-display font-black text-[var(--brand-navy)]">Next Ride / Yatra will be shown here</h3>
            <p className="mt-2 text-slate-600 text-sm max-w-lg mx-auto">Our team is preparing the next scheduled departures. Meanwhile, book a custom trip or a private vehicle — we'll get you there.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link to="/custom-booking" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--brand-orange)] hover:bg-[var(--brand-orange-2)] text-white font-bold text-xs" data-testid="empty-custom-cta">Plan a Custom Trip <ArrowRight className="w-3.5 h-3.5" /></Link>
              <Link to="/book" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--brand-navy)] text-white font-bold text-xs" data-testid="empty-book-cta">Book Vehicle</Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {routes.map((r,i) => {
              const isFeatured = featured && featured.id === r.id;
              return (
              <div key={r.id} className={`relative p-5 rounded-xl bg-white border transition-colors ${isFeatured ? "border-[var(--brand-orange)] ring-2 ring-[var(--brand-orange)]/20 shadow-lg" : "border-slate-200 hover:border-[var(--brand-orange)]"}`} data-testid={`route-card-${r.id}`}>
                {isFeatured && (
                  <div className="absolute -top-3 left-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-[var(--brand-orange)] to-amber-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-md" data-testid="featured-route-ribbon">
                    🔥 Most booked · {featured.bookings_last_week} this week
                  </div>
                )}
                <RouteSignCard from={r.from_place.toUpperCase()} to={r.to_place.toUpperCase()} tone={i%2===0?"navy":"orange"} subtitle={`${r.departure_time} · ${r.vehicle_type}`} />
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.seat_capacity} seats</span>
                  <span className="px-2 rounded-full bg-slate-100">{r.ac?"AC":"Non-AC"}</span>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div className="text-2xl font-black text-[var(--brand-navy)]">₹{r.price_per_seat}<span className="text-xs font-normal text-slate-500">/seat</span></div>
                  <Link to={`/book?from_place=${r.from_place}&to_place=${r.to_place}&travel_date=${new Date().toISOString().slice(0,10)}&passengers=1`} className="text-sm font-semibold text-[var(--brand-orange)] hover:underline">Book Seat →</Link>
                </div>
              </div>
            );})}
          </div>
        )}
      </section>

      {/* CTA — compact */}
      <section className="max-w-7xl mx-auto px-4 mt-16 mb-4">
        <div className="relative overflow-hidden rounded-2xl bg-[var(--brand-navy)] text-white px-6 py-6 md:px-10 md:py-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-display font-black tracking-tight">Corporate transfers, weddings &amp; customised tours</h2>
            <p className="mt-1 text-white/80 text-sm">Quotes in under 5 minutes. Multi-day trips, airport pickups &amp; outstation runs handled end-to-end.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {company.phone && <a href={`tel:${company.phone}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--brand-orange)] hover:bg-[var(--brand-orange-2)] font-bold text-xs hover:-translate-y-0.5" data-testid="call-cta"><Phone className="w-3.5 h-3.5" /> Call</a>}
            {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] hover:bg-emerald-600 font-bold text-xs hover:-translate-y-0.5" data-testid="wa-cta"><WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp</a>}
            <Link to="/book" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[var(--brand-navy)] font-bold text-xs hover:-translate-y-0.5" data-testid="book-vehicle-cta">Book Vehicle <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US slider */}
      <WhyChooseUs />
    </PublicLayout>
  );
}
